import React, { useEffect, useMemo, useState } from 'react';
import {
  Button,
  Field,
  Input,
  Dropdown,
  Option,
  makeStyles,
  shorthands,
  tokens,
  MessageBar,
  MessageBarBody,
} from '@fluentui/react-components';
import { ArrowSync20Regular } from '@fluentui/react-icons';
import { BaseDialog } from './BaseDialog';
import {
  StaffUser,
  UserRole,
  USER_ROLES,
  CreateUserInput,
  UpdateUserInput,
} from '../services/userService';

const useStyles = makeStyles({
  form: {
    display: 'flex',
    flexDirection: 'column',
    ...shorthands.gap('16px'),
  },
  row: {
    display: 'flex',
    ...shorthands.gap('12px'),
    '@media (max-width: 600px)': {
      flexDirection: 'column',
    },
  },
  half: {
    flex: 1,
    minWidth: 0,
  },
  passwordRow: {
    display: 'flex',
    alignItems: 'flex-end',
    ...shorthands.gap('8px'),
  },
  hint: {
    fontSize: '12px',
    color: tokens.colorNeutralForeground3,
  },
});

// A readable temp password the admin can hand off: letters + digits, no
// ambiguous chars, comfortably above the backend's 8-char minimum.
function generateTempPassword(): string {
  const alphabet = 'ABCDEFGHJKMNPQRSTUVWXYZabcdefghijkmnpqrstuvwxyz23456789';
  const bytes = new Uint32Array(12);
  crypto.getRandomValues(bytes);
  return Array.from(bytes, (b) => alphabet[b % alphabet.length]).join('');
}

interface UserFormDialogProps {
  isOpen: boolean;
  mode: 'create' | 'edit';
  /** The row being edited (mode === 'edit'). */
  user?: StaffUser | null;
  /** The acting admin's role — gates whether "Owner" is selectable. */
  actorRole: UserRole;
  saving?: boolean;
  error?: string | null;
  onClose: () => void;
  onSubmitCreate: (values: CreateUserInput) => void;
  onSubmitEdit: (values: UpdateUserInput) => void;
}

export const UserFormDialog: React.FC<UserFormDialogProps> = ({
  isOpen,
  mode,
  user,
  actorRole,
  saving,
  error,
  onClose,
  onSubmitCreate,
  onSubmitEdit,
}) => {
  const styles = useStyles();
  const [firstname, setFirstname] = useState('');
  const [lastname, setLastname] = useState('');
  const [email, setEmail] = useState('');
  const [role, setRole] = useState<UserRole>('office');
  const [phone, setPhone] = useState('');
  const [jobTitle, setJobTitle] = useState('');
  const [department, setDepartment] = useState('');
  const [password, setPassword] = useState('');

  // Seed the form whenever it opens (or the target row changes).
  useEffect(() => {
    if (!isOpen) return;
    if (mode === 'edit' && user) {
      setFirstname(user.firstname || '');
      setLastname(user.lastname || '');
      setEmail(user.email || '');
      setRole(user.role);
      setPhone(user.phone || '');
      setJobTitle(user.job_title || '');
      setDepartment(user.department || '');
      setPassword('');
    } else {
      setFirstname('');
      setLastname('');
      setEmail('');
      setRole('office');
      setPhone('');
      setJobTitle('');
      setDepartment('');
      setPassword(generateTempPassword());
    }
  }, [isOpen, mode, user]);

  // Only an owner may grant the owner role. When a non-owner edits an existing
  // owner the backend blocks the save anyway, but keep the option visible so the
  // current value renders correctly.
  const roleOptions = useMemo(
    () => USER_ROLES.filter((r) => r.value !== 'owner' || actorRole === 'owner' || user?.role === 'owner'),
    [actorRole, user],
  );

  const emailValid = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim());
  const nameValid = firstname.trim().length > 0 || lastname.trim().length > 0;
  const passwordValid = mode === 'edit' || password.length >= 8;
  const canSubmit = emailValid && nameValid && passwordValid && !saving;

  const handleSubmit = () => {
    if (!canSubmit) return;
    if (mode === 'create') {
      onSubmitCreate({
        email: email.trim(),
        firstname: firstname.trim(),
        lastname: lastname.trim(),
        role,
        password,
        phone: phone.trim() || undefined,
        job_title: jobTitle.trim() || undefined,
        department: department.trim() || undefined,
      });
    } else {
      onSubmitEdit({
        email: email.trim(),
        firstname: firstname.trim(),
        lastname: lastname.trim(),
        role,
        phone: phone.trim(),
        job_title: jobTitle.trim(),
        department: department.trim(),
      });
    }
  };

  const roleLabel = USER_ROLES.find((r) => r.value === role)?.label || role;

  return (
    <BaseDialog
      isOpen={isOpen}
      onClose={onClose}
      title={mode === 'create' ? 'Add user' : 'Edit user'}
      maxWidth="560px"
      showCloseButton
      actions={
        <>
          <Button appearance="secondary" onClick={onClose} disabled={saving}>
            Cancel
          </Button>
          <Button appearance="primary" onClick={handleSubmit} disabled={!canSubmit}>
            {saving ? 'Saving…' : mode === 'create' ? 'Create user' : 'Save changes'}
          </Button>
        </>
      }
    >
      <div className={styles.form}>
        {error && (
          <MessageBar intent="error">
            <MessageBarBody>{error}</MessageBarBody>
          </MessageBar>
        )}

        <div className={styles.row}>
          <Field label="First name" className={styles.half}>
            <Input
              size="large"
              value={firstname}
              onChange={(_, d) => setFirstname(d.value)}
              placeholder="Jane"
            />
          </Field>
          <Field label="Last name" className={styles.half}>
            <Input
              size="large"
              value={lastname}
              onChange={(_, d) => setLastname(d.value)}
              placeholder="Doe"
            />
          </Field>
        </div>

        <Field
          label="Email"
          required
          validationState={email && !emailValid ? 'error' : 'none'}
          validationMessage={email && !emailValid ? 'Enter a valid email address' : undefined}
        >
          <Input
            size="large"
            type="email"
            value={email}
            onChange={(_, d) => setEmail(d.value)}
            placeholder="jane@tradewindsmechanical.com"
          />
        </Field>

        <Field label="Role" required>
          <Dropdown
            size="large"
            selectedOptions={[role]}
            value={roleLabel}
            onOptionSelect={(_, d) => d.optionValue && setRole(d.optionValue as UserRole)}
          >
            {roleOptions.map((r) => (
              <Option key={r.value} value={r.value} text={r.label}>
                {r.label}
              </Option>
            ))}
          </Dropdown>
        </Field>

        <div className={styles.row}>
          <Field label="Job title" className={styles.half}>
            <Input
              size="large"
              value={jobTitle}
              onChange={(_, d) => setJobTitle(d.value)}
            />
          </Field>
          <Field label="Department" className={styles.half}>
            <Input
              size="large"
              value={department}
              onChange={(_, d) => setDepartment(d.value)}
            />
          </Field>
        </div>

        <Field label="Phone">
          <Input size="large" value={phone} onChange={(_, d) => setPhone(d.value)} />
        </Field>

        {mode === 'create' && (
          <Field
            label="Temporary password"
            required
            validationState={password && !passwordValid ? 'error' : 'none'}
            validationMessage={
              password && !passwordValid ? 'Must be at least 8 characters' : undefined
            }
            hint="Share this with the user — they can change it from their profile after signing in."
          >
            <div className={styles.passwordRow}>
              <Input
                size="large"
                style={{ flex: 1 }}
                value={password}
                onChange={(_, d) => setPassword(d.value)}
              />
              <Button
                size="large"
                icon={<ArrowSync20Regular />}
                onClick={() => setPassword(generateTempPassword())}
                title="Generate a new password"
              >
                Generate
              </Button>
            </div>
          </Field>
        )}
      </div>
    </BaseDialog>
  );
};

export default UserFormDialog;
